import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    job_number: '',
    client_id: '',
    location_ids: [], // Changed to array for multi-select
    department_id: '',
    job_date: new Date().toISOString().split('T')[0],
    job_name: '',
    description: '',
    financial_year_id: '',
    account_manager_id: '',
    project_manager_id: '',
    notes: '',
  });

  const [lineItems, setLineItems] = useState([
    { description: '', quantity: 1, in_rate: 0, out_rate: 0, quote_in: 0, quote_out: 0, profit: 0 },
  ]);

  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchClients();
    fetchDepartments();
    fetchUsers();
    if (isEditMode) {
      fetchJob();
    }
  }, [id]);

  useEffect(() => {
    if (formData.client_id) {
      fetchLocations(formData.client_id);
    }
  }, [formData.client_id]);

  const fetchJob = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/jobs/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch job');

      const data = await response.json();
      // API returns job directly, not wrapped in a 'job' property
      const job = data.job || data;
      setFormData({
        job_number: job.job_number || '',
        client_id: job.client_id,
        location_ids: job.location_id ? [job.location_id] : [], // Convert single to array for backward compatibility
        department_id: job.department_id || '',
        job_date: job.job_date ? (job.job_date.split('T')[0] || job.job_date) : new Date().toISOString().split('T')[0],
        job_name: job.job_name || '',
        description: job.description || '',
        financial_year_id: job.financial_year_id || '',
        account_manager_id: job.account_manager_id || '',
        project_manager_id: job.project_manager_id || '',
        notes: job.notes || '',
      });

      const lineItems = data.line_items || data.job?.line_items || [];
      if (lineItems && lineItems.length > 0) {
        setLineItems(lineItems);
      }
    } catch (err) {
      setError('Failed to load job');
      console.error(err);
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/clients`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch clients');

      const data = await response.json();
      setClients(data.clients || []);
    } catch (err) {
      console.error('Failed to load clients:', err);
    }
  };

  const fetchLocations = async (clientId) => {
    try {
      const token = localStorage.getItem('accessToken');
      // Use dealer-locations-with-addresses and filter by group_id (client_id)
      const response = await fetch(`${API_URL}/dealer-locations-with-addresses`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch locations');

      const allLocations = await response.json();
      // Filter locations by group_id (which is the client_id in consolidated model)
      const clientLocations = allLocations.filter(loc => loc.group_id === clientId);
      setLocations(clientLocations.map(loc => ({
        id: loc.id,
        location_name: loc.name || loc.trading_name,
      })));
    } catch (err) {
      console.error('Failed to load locations:', err);
      setLocations([]);
    }
  };

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/departments`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch departments');

      const data = await response.json();
      setDepartments(data.departments || []);
    } catch (err) {
      console.error('Failed to load departments:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch users');

      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const calculateLineItem = (quantity, inRate, outRate) => {
    const quoteIn = quantity * inRate;
    const quoteOut = quantity * outRate;
    const profit = quoteOut - quoteIn;
    return { quoteIn, quoteOut, profit };
  };

  const handleLineItemChange = (index, field, value) => {
    const updatedItems = [...lineItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };

    if (field === 'quantity' || field === 'in_rate' || field === 'out_rate') {
      const { quoteIn, quoteOut, profit } = calculateLineItem(
        updatedItems[index].quantity,
        updatedItems[index].in_rate,
        updatedItems[index].out_rate
      );
      updatedItems[index].quote_in = quoteIn;
      updatedItems[index].quote_out = quoteOut;
      updatedItems[index].profit = profit;
    }

    setLineItems(updatedItems);
  };

  const handleAddLineItem = () => {
    setLineItems([
      ...lineItems,
      { description: '', quantity: 1, in_rate: 0, out_rate: 0, quote_in: 0, quote_out: 0, profit: 0 },
    ]);
  };

  const handleRemoveLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const calculateTotals = () => {
    const totalQuoteIn = lineItems.reduce((sum, item) => sum + (Number(item.quote_in) || 0), 0);
    const totalQuoteOut = lineItems.reduce((sum, item) => sum + (Number(item.quote_out) || 0), 0);
    const totalProfit = totalQuoteOut - totalQuoteIn;

    return {
      totalQuoteIn: totalQuoteIn.toFixed(2),
      totalQuoteOut: totalQuoteOut.toFixed(2),
      totalProfit: totalProfit.toFixed(2),
      profitMargin: totalQuoteOut > 0 ? ((totalProfit / totalQuoteOut) * 100).toFixed(1) : '0.0',
    };
  };

  const generateJobNumber = () => {
    const year = new Date().getFullYear();
    const timestamp = Date.now().toString().slice(-6);
    return `JOB-${year}-${timestamp}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('accessToken');
      const url = isEditMode ? `${API_URL}/jobs/${id}` : `${API_URL}/jobs`;
      const method = isEditMode ? 'PUT' : 'POST';

      // Auto-generate job number if not provided (for new jobs only)
      // For now, use first location_id for backward compatibility with backend
      // TODO: Enhance backend to support multiple locations via job_locations junction table
      const submitData = {
        ...formData,
        job_number: formData.job_number || (isEditMode ? formData.job_number : generateJobNumber()),
        location_id: formData.location_ids && formData.location_ids.length > 0 ? formData.location_ids[0] : null,
        location_ids: undefined, // Remove from payload as backend doesn't support it yet
        line_items: lineItems,
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(submitData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save job');
      }

      alert(isEditMode ? 'Job updated successfully' : 'Job created successfully');
      navigate('/joblist/jobs');
    } catch (err) {
      setError(err.message);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConvertToInvoice = async () => {
    if (!window.confirm('Convert this job to an invoice? This will create a new draft invoice with the job details.')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('accessToken');

      // Generate invoice number
      const invoiceNumber = `INV-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`;

      // Convert job line items to invoice line items
      const invoiceLineItems = lineItems.map((item) => ({
        job_id: id,
        product_id: item.product_id,
        description: item.description,
        quantity: item.quantity,
        unit_price: item.out_rate,
        discount_percentage: 0,
      }));

      // Create invoice
      const response = await fetch(`${API_URL}/invoices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          invoice_number: invoiceNumber,
          client_id: formData.client_id,
          invoice_date: new Date().toISOString().split('T')[0],
          due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          financial_year_id: formData.financial_year_id || null,
          discount_percentage: 0,
          notes: `Generated from Job: ${formData.job_name}${formData.notes ? '\n\nJob Notes: ' + formData.notes : ''}`,
          line_items: invoiceLineItems.filter(item => item.description && item.description.trim() !== ''),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create invoice');
      }

      const data = await response.json();
      alert('Invoice created successfully from job');
      // API returns invoice directly or wrapped in invoice property
      const invoiceId = data.invoice?.id || data.id;
      navigate(`/joblist/invoices/${invoiceId}`);
    } catch (err) {
      setError(err.message);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading job...</div>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditMode ? 'Edit Job' : 'Create New Job'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isEditMode ? 'Update job details and line items' : 'Fill in the details below to create a new job'}
          </p>
        </div>
        <button
          onClick={() => navigate('/joblist/jobs')}
          className="px-4 py-2 text-gray-700 hover:text-gray-900"
        >
          ← Back to Jobs
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Job Details Card */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Job Details</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Number
              </label>
              <input
                type="text"
                value={formData.job_number}
                onChange={(e) => setFormData({ ...formData, job_number: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="Optional - leave blank for auto-generation"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.job_date}
                onChange={(e) => setFormData({ ...formData, job_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.client_id}
                onChange={(e) => setFormData({ ...formData, client_id: e.target.value, location_id: '' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                required
              >
                <option value="">Select client...</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.company_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Locations {formData.client_id && <span className="text-gray-500 text-xs">(can select multiple)</span>}
              </label>
              <select
                multiple
                value={formData.location_ids}
                onChange={(e) => {
                  const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                  setFormData({ ...formData, location_ids: selectedOptions });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent min-h-[100px]"
                disabled={!formData.client_id}
                size={locations.length > 0 ? Math.min(locations.length, 5) : 1}
              >
                {locations.length === 0 ? (
                  <option disabled>{formData.client_id ? 'No locations available' : 'Select a client first'}</option>
                ) : (
                  locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.location_name}
                    </option>
                  ))
                )}
              </select>
              {formData.location_ids.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {formData.location_ids.map((locId) => {
                    const loc = locations.find(l => l.id === locId);
                    return loc ? (
                      <span key={locId} className="inline-flex items-center px-2 py-1 rounded-md bg-emerald-100 text-emerald-800 text-sm">
                        {loc.location_name}
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              location_ids: formData.location_ids.filter(id => id !== locId)
                            });
                          }}
                          className="ml-1 text-emerald-600 hover:text-emerald-800"
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.job_name}
                onChange={(e) => setFormData({ ...formData, job_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Department
              </label>
              <select
                value={formData.department_id}
                onChange={(e) => setFormData({ ...formData, department_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="">Select department...</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Manager
              </label>
              <select
                value={formData.account_manager_id}
                onChange={(e) => setFormData({ ...formData, account_manager_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="">Select account manager...</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Project Manager
              </label>
              <select
                value={formData.project_manager_id}
                onChange={(e) => setFormData({ ...formData, project_manager_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="">Select project manager...</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                rows={3}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                rows={2}
              />
            </div>
          </div>
        </div>

        {/* Line Items Card */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Line Items</h2>
            <button
              type="button"
              onClick={handleAddLineItem}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm"
            >
              + Add Line Item
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cost (In)</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Price (Out)</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quote In</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quote Out</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Profit</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {lineItems.map((item, index) => (
                  <tr key={index}>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        placeholder="Item description"
                        required
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleLineItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                        min="0"
                        step="0.01"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        value={item.in_rate}
                        onChange={(e) => handleLineItemChange(index, 'in_rate', parseFloat(e.target.value) || 0)}
                        className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                        min="0"
                        step="0.01"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        value={item.out_rate}
                        onChange={(e) => handleLineItemChange(index, 'out_rate', parseFloat(e.target.value) || 0)}
                        className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                        min="0"
                        step="0.01"
                      />
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      £{Number(item.quote_in).toFixed(2)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      £{Number(item.quote_out).toFixed(2)}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`text-sm font-semibold ${Number(item.profit) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        £{Number(item.profit).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <button
                        type="button"
                        onClick={() => handleRemoveLineItem(index)}
                        className="text-red-600 hover:text-red-900 text-sm"
                        disabled={lineItems.length === 1}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals Summary */}
          <div className="mt-6 flex justify-end">
            <div className="w-96 bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">Total Quote In (Costs):</span>
                <span className="text-lg font-semibold text-red-600">£{totals.totalQuoteIn}</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">Total Quote Out (Revenue):</span>
                <span className="text-lg font-semibold text-blue-600">£{totals.totalQuoteOut}</span>
              </div>
              <div className="border-t border-gray-300 pt-2 mt-2">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-base font-medium text-gray-900">Total Profit:</span>
                  <span className={`text-xl font-bold ${parseFloat(totals.totalProfit) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    £{totals.totalProfit}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Profit Margin:</span>
                  <span className="text-sm font-semibold text-gray-700">{totals.profitMargin}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center gap-4">
          <div>
            {isEditMode && (
              <button
                type="button"
                onClick={handleConvertToInvoice}
                disabled={loading}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                Convert to Invoice
              </button>
            )}
          </div>
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/joblist/jobs')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Saving...' : isEditMode ? 'Update Job' : 'Create Job'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

